import React, { useState, useMemo } from 'react';
import { 
  Box, 
  Typography, 
  Button, 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions, 
  Snackbar, 
  Alert,
  Card,
  CardContent,
  CardHeader,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  Grid,
  Paper,
  useMediaQuery,
  useTheme
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { useAuth } from '../contexts/AuthContext';
import PhoneIcon from '@mui/icons-material/Phone';
import WarningIcon from '@mui/icons-material/Warning';
import SecurityIcon from '@mui/icons-material/Security';
import ReportIcon from '@mui/icons-material/Report';
import HelpIcon from '@mui/icons-material/Help';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import GavelIcon from '@mui/icons-material/Gavel';
import AlarmIcon from '@mui/icons-material/Alarm';
import InfoIcon from '@mui/icons-material/Info';
import PsychologyAltIcon from '@mui/icons-material/PsychologyAlt';
import NotificationImportantIcon from '@mui/icons-material/NotificationImportant';
import LocalHospitalIcon from '@mui/icons-material/LocalHospital';
import BusinessIcon from '@mui/icons-material/Business';

const SOSIconContainer = styled(Box)(({ theme, showTooltip }) => ({
  position: 'fixed',
  bottom: '660px', // Positioned above ReportIcon (which is at 500px)
  right: '20px',
  zIndex: showTooltip ? 9990 : 9998,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-end',
}));

const SOSIconOverlay = styled(Box)(({ theme }) => ({
  width: '60px',
  height: '60px',
  borderRadius: '50%',
  background: 'linear-gradient(135deg, #ff6b35 0%, #f7931e 100%)', // Orange gradient for emergency
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
  transition: 'all 0.3s ease-in-out',
  boxShadow: '0 4px 20px rgba(255, 107, 53, 0.3)',
  border: '2px solid rgba(255, 255, 255, 0.2)',
  animation: 'pulse 2s infinite',
  '&:hover': {
    transform: 'scale(1.05)',
    boxShadow: '0 6px 25px rgba(255, 107, 53, 0.5)',
  },
  '@keyframes pulse': {
    '0%': {
      boxShadow: '0 4px 20px rgba(255, 107, 53, 0.3)',
    },
    '50%': {
      boxShadow: '0 4px 20px rgba(255, 107, 53, 0.6)',
    },
    '100%': {
      boxShadow: '0 4px 20px rgba(255, 107, 53, 0.3)',
    },
  },
}));

const ContactIconOverlay = styled(Box)(({ theme }) => ({
  width: '50px',
  height: '50px',
  borderRadius: '50%',
  background: 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)', // Blue gradient for contacts
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
  transition: 'all 0.3s ease-in-out',
  boxShadow: '0 4px 20px rgba(25, 118, 210, 0.3)',
  border: '2px solid rgba(255, 255, 255, 0.2)',
  '&:hover': {
    transform: 'scale(1.05)',
    boxShadow: '0 6px 25px rgba(25, 118, 210, 0.5)',
  },
}));

const SOSTooltip = styled(Box)(({ theme, isMobile }) => ({
  position: 'absolute',
  bottom: isMobile ? '15px' : '10px',
  right: isMobile ? '70px' : '80px',
  width: isMobile ? '260px' : '280px',
  background: 'linear-gradient(135deg, #f7931e 0%, #e65100 100%)', // Orange gradient
  border: '2px solid #ff6b35', // Orange border
  borderRadius: '12px',
  padding: isMobile ? theme.spacing(1.5) : theme.spacing(2),
  zIndex: 10014,
  boxShadow: '0 12px 40px rgba(255, 107, 53, 0.4)',
  backdropFilter: 'blur(10px)',
  animation: 'tooltipSlideIn 0.3s ease-out',
  '&::before': {
    content: '""',
    position: 'absolute',
    bottom: isMobile ? '25px' : '20px',
    right: isMobile ? '-8px' : '-10px',
    width: '0',
    height: '0',
    borderTop: isMobile ? '8px' : '10px',
    borderTop: `${isMobile ? '8px' : '10px'} solid transparent`,
    borderBottom: `${isMobile ? '8px' : '10px'} solid transparent`,
    borderLeft: `${isMobile ? '8px' : '10px'} solid #f7931e`, // Orange arrow pointing right
  },
}));

// Constants
const CENTRAL_ONLINEWACHE = "https://portal.onlinewache.polizei.de";
const VERBRAUCHERZENTRALE = "https://www.verbraucherzentrale.de/beratung";
const ZAC_WEBSITE = "https://www.polizei.de/Polizei/DE/Einrichtungen/ZAC/zac_node.html";
//const ZAC_EMAIL = "mailto:meldestelle@bsi.bund.de";
const BNETZA = "https://www.bundesnetzagentur.de/DE/Vportal/TK/Aerger/start.html";
const NOTRUF_116116 = "tel:116116";

// Contact categories and numbers
const CONTACT_CATEGORIES = [
  {
    title: "Notruf & Polizei",
    icon: <NotificationImportantIcon />,
    contacts: [
      { name: "Polizei Notruf", number: "110", description: "Bei akuten Bedrohungen" },
      { name: "Feuerwehr & Rettung", number: "112", description: "Medizinische Notfälle" },
      { name: "Ärztlicher Bereitschaftsdienst", number: "116 117", description: "Außerhalb der Praxiszeiten" }
    ]
  },
  {
    title: "Psychische & Soziale Krisen",
    icon: <PsychologyAltIcon />,
    contacts: [

      { name: "Telefonseelsorge", number: "116 123", description: "Seelischer Beistand für Anrufende, die unter Einsamkeit leiden, eine Lebenskrise durchmachen oder Suizidgedanken hegen." },
      { name: "Hilfetelefon: Vermisste Kinder", number: "116 000", description: "Der Dienst nimmt Meldungen über vermisste Kinder entgegen und leitet sie an die Polizei weiter, berät und unterstützt die für vermisste Kinder verantwortlichen Personen, unterstützt die Untersuchung" },
      { name: "Nummer gegen Kummer (für Kinder & Jugendliche)", number: "116 111", description: "Sorgen und Probleme, Notsituationen von Kindern und Jugendlichen." },
      { name: "Nummer gegen Kummer (für Eltern)", number: "0800 111 0 550", description: "Das Elterntelefon ist für alle Eltern und andere Erziehende da, die sich Sorgen um Ihr Kind machen, sich überfordert oder manchmal hilflos fühlen." },
      { name: "Hilfetelefon: Gewalt gegen Frauen", number: "116 016", description: "Beratung und Unterstützung für gewaltbetroffene Frauen " },  
      { name: "Hilfetelefon: Gewalt gegen Männer", number: "0800 123 99 00", description: "Beratung und Unterstützung für gewaltbetroffene Männer" },
      { name: "Hilfetelefon: Sexueller Missbrauch", number: "0800 22 55 530", description: "Anlaufstelle für Betroffene, Angehörige und Fachkräfte, die psychologische und pädagogische Unterstützung bei Fragen und Sorgen rund um sexuelle Gewalt in Kindheit und Jugend suchen" },  
      { name: "Hilfetelefon: Schwangere und Familie in Not", number: "0800 40 40 020", description: "Schwangere, ihr direktes Umfeld und auch Fachkräfte können sich vor und nach der Geburt rund um die Uhr an die Beraterinnen wenden, die passende Hilfsangebote kennen" },
      { name: "Hilfetelefon: Weisser Ring ", number: "116 006", description: "Beratungsdienst für Opfer von Verbrechen" },
      { name: "Silbernetz (für ältere Menschen gegen Einsamkeit)", number: "0800 4 70 80 90", description: "Silbernetz bahnt einsamen Menschen ab 60 Jahren Wege aus der Isolation." },
    ]
  },

{
    title: "Gesundheit & Beratung",
    icon: <LocalHospitalIcon />,
    contacts: [

      { name: "Pflegeberatung", number: "0800 101 88 00 ", description: "Pflegesituation organisieren, sich in der Pflegelandschaft orientieren oder sich auf die Pflege vorbereiten." },
      { name: "Patientenberatung (UPD)", number: "0800 011 77 22", description: "Menschen dabei unterstützen, zu gesundheitlichen und gesundheitsrechtlichen Themen den Überblick zu behalten" },
      { name: "Bundesweite Sucht & Drogen Hotline", number: "01806 313031", description: "Telefonische Beratung, Hilfe und Informationen durch erfahrene Fachleute aus der Drogen- und Suchthilfe. 0,20€ pro Anruf." },
    ]
  },

  {
    title: "Arbeit, Soziales, Behörden",
    icon: <BusinessIcon />,
    contacts: [

      { name: "Bürgertelefon der Bundesregierung", number: "0030 18 272 2720", description: "Wegweiser durch die Zuständigkeiten innerhalb der Bundesregierung" },
      { name: "Bundesagentur für Arbeit", number: "0800 4 5555 00", description: "Arbeit, Weiterbildung, Arbeitslosengeld" },
      { name: "Deutsche Rentenversicherung", number: "0800 1000 4800", description: "Fragen zu Rente, Rentenpunkten, Beratungsterminen" },
      { name: "Behördennummer", number: "115", description: "Mit der Behördennummer entfällt für Sie die aufwendige Suche nach dem zuständigen Amt, den Telefonnummern und Öffnungszeiten" },
    ]
  },
];

const LANDER = [
  { id: "bw", name: "Baden-Württemberg", url: "https://www.polizei-bw.de/internetwache/" },
  { id: "by", name: "Bayern", url: "https://www.bayernportal.de/dokumente/onlineverfahren/305652977295" },
  { id: "be", name: "Berlin", url: "https://www.internetwache-polizei-berlin.de/" },
  { id: "bb", name: "Brandenburg", url: "https://polizei.brandenburg.de/liste/uebersicht/1705377" },
  { id: "hb", name: "Bremen", url: "https://www.polizei.bremen.de/onlinewache-53847" },
  { id: "hh", name: "Hamburg", url: "https://www.polizei.hamburg/onlinewache-der-polizei-hamburg" },
  { id: "he", name: "Hessen", url: "https://www.polizei.hessen.de/service/onlinewache/" },
  { id: "mv", name: "Mecklenburg-Vorpommern", url: "https://www.polizei.mvnet.de/onlinewache/?racr=a" },
  { id: "ni", name: "Niedersachsen", url: "https://www.polizei-nds.de/startseite/wir_uber_uns/onlinewache/onlinewache-der-polizei-niedersachsen-112182.html" },
  { id: "nw", name: "Nordrhein-Westfalen", url: "https://internetwache.polizei.nrw/" },
  { id: "rp", name: "Rheinland-Pfalz", url: "https://portal.onlinewache.polizei.de/de/rp/" },
  { id: "sl", name: "Saarland", url: "https://www.saarland.de/polizei/DE/onlinewache/onlinewache_node.html" },
  { id: "sn", name: "Sachsen", url: "https://www.polizei.sachsen.de/de/onlinewache-anzeige-erstatten.htm" },
  { id: "st", name: "Sachsen-Anhalt", url: "https://polizei.sachsen-anhalt.de/das-sind-wir/polizei-interaktiv/e-revier" },
  { id: "sh", name: "Schleswig-Holstein", url: "https://www.polizei.schleswig-holstein.de/internetwache/" },
  { id: "th", name: "Thüringen", url: "https://portal.onlinewache.polizei.de/de/th/" },
];

// Decision outcomes - will be created dynamically to use current police URL

// Helper component: Step item
const Step = ({ n, title, children, icon: Icon, isMobile }) => (
  <Box sx={{ display: 'flex', gap: isMobile ? 1.5 : 2, mb: isMobile ? 2 : 3 }}>
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
      <Box sx={{ 
        width: isMobile ? 32 : 36, 
        height: isMobile ? 32 : 36, 
        borderRadius: '50%', 
        backgroundColor: '#fff3e0', 
        color: '#e65100',
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center', 
        fontWeight: 'bold',
        boxShadow: 1,
        fontSize: isMobile ? '0.9rem' : '1rem'
      }}>
        {n}
      </Box>
      <Box sx={{ 
        width: 2, 
        height: isMobile ? 30 : 40, 
        background: 'linear-gradient(to bottom, #ffcc02, transparent)',
        mt: 1
      }} />
    </Box>
    <Box sx={{ flex: 1, pb: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        {Icon && <Icon sx={{ color: '#e65100', fontSize: isMobile ? 18 : 20 }} />}
        <Typography variant={isMobile ? "subtitle1" : "h6"} sx={{ 
          fontWeight: 'bold',
          fontSize: isMobile ? '1rem' : '1.25rem'
        }}>{title}</Typography>
      </Box>
      <Typography variant="body2" sx={{ 
        color: 'text.secondary',
        fontSize: isMobile ? '0.85rem' : '0.875rem',
        lineHeight: 1.4
      }}>{children}</Typography>
    </Box>
  </Box>
);

// Helper: Action card
const ActionCard = ({ title, desc, cta, href, icon: Icon, note, isMobile }) => (
  <Card sx={{ 
    height: '100%', 
    transition: 'all 0.3s ease',
    '&:hover': { 
      boxShadow: 4,
      transform: 'translateY(-2px)'
    }
  }}>
    <CardHeader 
      title={
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {Icon && <Icon sx={{ fontSize: isMobile ? 18 : 20 }} />}
          <Typography variant={isMobile ? "subtitle1" : "h6"} sx={{ 
            fontSize: isMobile ? '0.9rem' : '1rem',
            fontWeight: 'bold'
          }}>
            {title}
          </Typography>
        </Box>
      }
      sx={{ pb: 1 }}
    />
    <CardContent sx={{ pt: 0 }}>
      <Typography variant="body2" sx={{ 
        color: 'text.secondary', 
        mb: 2,
        fontSize: isMobile ? '0.8rem' : '0.875rem',
        lineHeight: 1.4
      }}>
        {desc}
      </Typography>
      <Button 
        variant="contained" 
        size={isMobile ? "small" : "small"}
        href={href}
        target="_blank"
        rel="noreferrer"
        endIcon={<ArrowForwardIcon />}
        sx={{ 
          background: 'linear-gradient(135deg, #ff6b35 0%, #f7931e 100%)',
          fontSize: isMobile ? '0.75rem' : '0.875rem',
          '&:hover': {
            background: 'linear-gradient(135deg, #f7931e 0%, #e65100 100%)',
          }
        }}
      >
        {cta}
      </Button>
      {note && (
        <Typography variant="caption" sx={{ 
          color: 'text.secondary', 
          mt: 1, 
          display: 'block',
          fontSize: isMobile ? '0.7rem' : '0.75rem'
        }}>
          {note}
        </Typography>
      )}
    </CardContent>
  </Card>
);

const SOSIconComponent = () => {
  const { user } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [showTooltip, setShowTooltip] = useState(false);
  const [showSOSDialog, setShowSOSDialog] = useState(false);
  const [showContactDialog, setShowContactDialog] = useState(false);
  const [bundesland, setBundesland] = useState("");
  const [decision, setDecision] = useState(null);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  const getCurrentPoliceUrl = () => {
    if (!bundesland) return CENTRAL_ONLINEWACHE;
    const selectedLand = LANDER.find(land => land.id === bundesland);
    return selectedLand ? selectedLand.url : CENTRAL_ONLINEWACHE;
  };

  const outcomes = useMemo(() => ({
    moneyLost: {
      title: "Geld verloren oder überwiesen",
      actions: [
        { label: "Bank/Karte sofort sperren – 116 116 anrufen", href: NOTRUF_116116 },
        { label: "Anzeige bei der Polizei erstatten (Onlinewache)", href: getCurrentPoliceUrl() },
        { label: "Belege sammeln & Verbraucherzentrale um Rat fragen", href: VERBRAUCHERZENTRALE },
      ],
    },
    phishing: {
      title: "Phishing / Zugangsdaten preisgegeben",
      actions: [
        { label: "Passwörter sofort ändern", href: "#passworttipps" },
        { label: "Phishing bei der ZAC melden", href: ZAC_WEBSITE },
        { label: "Verdächtige Rufnummern bei der Bundesnetzagentur melden", href: BNETZA },
      ],
    },
    malware: {
      title: "Verdacht auf Schadsoftware",
      actions: [
        { label: "Gerät trennen & Virenscan ausführen (aktualisieren)", href: "#" },
        { label: "ZAC-Hinweise lesen", href: ZAC_WEBSITE },
        { label: "Wichtige Konten prüfen (E-Mail, Banking)", href: "#" },
      ],
    },
    fakeShop: {
      title: "Fake‑Shop / Paket‑SMS / Abo‑Falle",
      actions: [
        { label: "Zahlungen stoppen / Rückbuchung versuchen", href: NOTRUF_116116 },
        { label: "Polizei informieren (Onlinewache)", href: getCurrentPoliceUrl() },
        { label: "Beschwerde an Bundesnetzagentur (Spam, SMS)", href: BNETZA },
      ],
    },
  }), [bundesland]);

  const selectedOutcome = useMemo(() => (decision ? outcomes[decision] : null), [decision, outcomes]);

  const handleSOSClick = () => {
    setShowSOSDialog(true);
    setShowTooltip(false); // Hide tooltip when opening dialog
  };

  const handleContactClick = () => {
    setShowContactDialog(true);
    setShowTooltip(false); // Hide tooltip when opening dialog
  };

  const handleSnackbarClose = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const handleCloseDialog = () => {
    setShowSOSDialog(false);
    setShowTooltip(false);
    setDecision(null);
    setBundesland("");
  };

  const handleCloseContactDialog = () => {
    setShowContactDialog(false);
    setShowTooltip(false);
  };

  if (!user) {
    return null;
  }

  return (
    <>
      <SOSIconContainer showTooltip={showTooltip}>
        <SOSIconOverlay
          onClick={handleSOSClick}
          onMouseEnter={() => setShowTooltip(true)}
          onMouseLeave={() => setShowTooltip(false)}
          onTouchStart={() => {
            // On mobile, show tooltip briefly then hide it
            setShowTooltip(true);
            setTimeout(() => setShowTooltip(false), 2000);
          }}
        >
          <Typography 
            sx={{ 
              color: 'white', 
              fontSize: '16px', 
              fontWeight: 'bold',
              letterSpacing: '1px',
              textShadow: '0 1px 2px rgba(0,0,0,0.3)'
            }}
          >
            SOS
          </Typography>
        </SOSIconOverlay>
        
        {showTooltip && (
          <SOSTooltip isMobile={isMobile}>
            <Typography variant="body2" sx={{ 
              color: 'white', 
              fontWeight: 'bold', 
              mb: 1,
              fontSize: isMobile ? '0.9rem' : '0.875rem'
            }}>
              Online-Betrug Hilfe
            </Typography>
            <Typography variant="caption" sx={{ 
              color: 'rgba(255, 255, 255, 0.9)',
              fontSize: isMobile ? '0.75rem' : '0.75rem',
              lineHeight: 1.3
            }}>
              Sofortige Hilfe bei Cyber-Betrug und Online-Kriminalität
            </Typography>
          </SOSTooltip>
        )}
      </SOSIconContainer>

      {/* Comprehensive Fraud Help Dialog */}
      <Dialog
        open={showSOSDialog}
        onClose={handleCloseDialog}
        maxWidth="lg"
        fullWidth
        fullScreen={isMobile}
        PaperProps={{
          sx: {
            borderRadius: isMobile ? 0 : '16px',
            background: 'linear-gradient(135deg, #fff8e1 0%, #ffffff 100%)',
            maxHeight: isMobile ? '100vh' : '90vh',
            margin: isMobile ? 0 : '32px'
          }
        }}
      >
        <DialogTitle sx={{ 
          background: 'linear-gradient(135deg, #ff6b35 0%, #f7931e 100%)',
          color: 'white',
          textAlign: 'center',
          py: isMobile ? 2 : 3
        }}>
          <Typography variant={isMobile ? "h5" : "h4"} component="div" sx={{ 
            fontWeight: 'bold', 
            mb: 1,
            fontSize: isMobile ? '1.5rem' : '2.125rem'
          }}>
            🚨 Online-Betrug · Schnellhilfe
          </Typography>
          <Typography variant="body1" sx={{ 
            opacity: 0.9,
            fontSize: isMobile ? '0.9rem' : '1rem'
          }}>
            Opfer von Online-Betrug? So bekommst du sofort Hilfe.
          </Typography>
        </DialogTitle>
        
        <DialogContent sx={{ 
          p: isMobile ? 2 : 3, 
          maxHeight: isMobile ? 'calc(100vh - 120px)' : '70vh', 
          overflow: 'auto' 
        }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: isMobile ? 2 : 4 }}>
            
            {/* Hero Section */}
            <Box sx={{ textAlign: 'center', mb: isMobile ? 2 : 3 }}>
              <Typography variant={isMobile ? "h6" : "h5"} sx={{ 
                fontWeight: 'bold', 
                mb: 2, 
                color: '#e65100',
                fontSize: isMobile ? '1.1rem' : '1.5rem',
                lineHeight: 1.3
              }}>
                3 goldene Regeln: Nicht zahlen • Beweise sichern • Sofort melden
              </Typography>
              <Box sx={{ 
                display: 'flex', 
                justifyContent: 'center', 
                gap: isMobile ? 1 : 2, 
                flexWrap: 'wrap', 
                alignItems: 'center' 
              }}>
                <Chip 
                  icon={<PhoneIcon />} 
                  label={isMobile ? "Polizei: 110" : "Polizei: 110"} 
                  color="error" 
                  size={isMobile ? "small" : "medium"}
                />
                <Chip 
                  icon={<PhoneIcon />} 
                  label={isMobile ? "Feuerwehr: 112" : "Feuerwehr und Rettungsdienst: 112"} 
                  color="error" 
                  size={isMobile ? "small" : "medium"}
                />
                <Chip 
                  icon={<PhoneIcon />} 
                  label={isMobile ? "Kartensperre: 116 116" : "Kartensperre: 116 116"} 
                  color="warning" 
                  size={isMobile ? "small" : "medium"}
                />
                <Chip 
                  icon={<PhoneIcon />} 
                  label={isMobile ? "Weitere Kontakte" : "Weitere wichtige Kontaktdaten"} 
                  color="primary"
                  size={isMobile ? "small" : "medium"}
                  onClick={handleContactClick}
                  sx={{
                    cursor: 'pointer',
                    '&:hover': {
                      backgroundColor: 'rgba(25, 118, 210, 0.12)'
                    }
                  }}
                />
              </Box>
            </Box>

            <Grid container spacing={isMobile ? 2 : 3}>
              {/* Left Column - Steps */}
              <Grid item xs={12} lg={7}>
                <Card>
                  <CardHeader>
                    <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <ReportIcon sx={{ color: '#e65100' }} />
                      Sofortmaßnahmen – Schritt für Schritt
                    </Typography>
                  </CardHeader>
                  <CardContent>
                    <Step n={1} title="Stoppen & Sichern" icon={SecurityIcon} isMobile={isMobile}>
                      Keine weiteren Zahlungen oder Daten preisgeben. Beweise sammeln: Screenshots, Mails, Chatverläufe, IBAN.
                    </Step>
                    <Step n={2} title="Bank/Karten sperren (bei Geldfluss)" icon={AccountBalanceIcon} isMobile={isMobile}>
                      Sofort bei deiner Bank melden oder zentrale Sperrnummer <a href={NOTRUF_116116} style={{ color: '#e65100', textDecoration: 'underline' }}>116 116</a> anrufen.
                    </Step>
                    <Step n={3} title="Anzeige erstatten" icon={GavelIcon} isMobile={isMobile}>
                      Online bei der Polizei deines Bundeslands. Nutze die <a href={CENTRAL_ONLINEWACHE} target="_blank" rel="noreferrer" style={{ color: '#e65100', textDecoration: 'underline' }}>Onlinewache</a>.
                    </Step>
                    <Step n={4} title="Beratung einholen" icon={HelpIcon} isMobile={isMobile}>
                      Rechtliche & praktische Hilfe bei der <a href={VERBRAUCHERZENTRALE} target="_blank" rel="noreferrer" style={{ color: '#e65100', textDecoration: 'underline' }}>Verbraucherzentrale</a>.
                    </Step>
                    <Step n={5} title="Technik prüfen" icon={CheckCircleIcon} isMobile={isMobile}>
                      Passwörter ändern, 2‑Faktor aktivieren, Geräte scannen. 
                    </Step>
                    <Step n={6} title="Andere Nutzer*innen informieren" icon={InfoIcon} isMobile={isMobile}>
                      Nicht vergessen: Andere Nutzer*innen über verdächtige Aktivitäten mit Hilfe unseres Meldeformulars zu informieren. 
                    </Step>
                  </CardContent>
                </Card>
              </Grid>

              {/* Right Column - Quick Actions */}
              <Grid item xs={12} lg={5}>
                <Card sx={{ mb: 3 }}>
                  <CardHeader>
                    <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <PhoneIcon sx={{ color: '#e65100' }} />
                      Direkt zur Zuständigkeit
                    </Typography>
                  </CardHeader>
                  <CardContent>
                    <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
                      Wähle dein Bundesland für die Onlinewache:
                    </Typography>
                    <FormControl fullWidth sx={{ mb: 2 }}>
                      <InputLabel>Bundesland</InputLabel>
                      <Select value={bundesland} onChange={(e) => setBundesland(e.target.value)}>
                        {LANDER.map(land => (
                          <MenuItem key={land.id} value={land.id}>{land.name}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </CardContent>
                </Card>

                <Grid container spacing={isMobile ? 1 : 2}>
                  <Grid item xs={12} sm={6}>
                    <ActionCard
                      title="Polizei (Onlinewache)"
                      desc="Für Bürger*innen:Strafanzeige erstatten – auch online. Zuständig für jeden Betrugsfall."
                      cta="Anzeige erstatten"
                      href={getCurrentPoliceUrl()}
                      icon={GavelIcon}
                      isMobile={isMobile}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <ActionCard
                      title="Verbraucherzentrale"
                      desc="Für Bürger*innen: Beratung zu Rückforderungen, Fake‑Shops, Abzocke."
                      cta="Zur Verbraucherzentrale"
                      href={VERBRAUCHERZENTRALE}
                      icon={HelpIcon}
                      note="*teilweise können Kosten anfallen"
                      isMobile={isMobile}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <ActionCard
                      title="ZAC – Zentrale Ansprechstelle Cybercrime"
                      desc="NUR für Wirtschaftsunternehmen: Informationen und Hilfe im Falle von Cybercrime-Straftaten gegen das Unternehmen"
                      cta="ZAC kontaktieren"
                      href={ZAC_WEBSITE}
                      icon={SecurityIcon}
                      isMobile={isMobile}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <ActionCard
                      title="Bundesnetzagentur"
                      desc="Für Bürger*innen: Rufnummern‑/SMS‑Missbrauch, Spam melden."
                      cta="Beschwerde einreichen"
                      href={BNETZA}
                      icon={PhoneIcon}
                      isMobile={isMobile}
                    />
                  </Grid>
                </Grid>
              </Grid>
            </Grid>

            {/* Decision Helper - COMMENTED OUT FOR REWORK */}
            {/* 
            <Card>
              <CardHeader>
                <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <HelpIcon sx={{ color: '#e65100' }} />
                  Was ist passiert? Entscheidungshilfe
                </Typography>
              </CardHeader>
              <CardContent>
                <Grid container spacing={2} sx={{ mb: 3 }}>
                  <Grid item xs={12} sm={6} md={3}>
                    <Button 
                      fullWidth
                      variant={decision === "moneyLost" ? "contained" : "outlined"}
                      onClick={() => setDecision("moneyLost")}
                      sx={{ 
                        justifyContent: 'flex-start',
                        background: decision === "moneyLost" ? 'linear-gradient(135deg, #ff6b35 0%, #f7931e 100%)' : 'transparent'
                      }}
                    >
                      💳 Geld verloren/überwiesen
                    </Button>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Button 
                      fullWidth
                      variant={decision === "phishing" ? "contained" : "outlined"}
                      onClick={() => setDecision("phishing")}
                      sx={{ 
                        justifyContent: 'flex-start',
                        background: decision === "phishing" ? 'linear-gradient(135deg, #ff6b35 0%, #f7931e 100%)' : 'transparent'
                      }}
                    >
                      ✉️ Phishing / Zugangsdaten geteilt
                    </Button>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Button 
                      fullWidth
                      variant={decision === "malware" ? "contained" : "outlined"}
                      onClick={() => setDecision("malware")}
                      sx={{ 
                        justifyContent: 'flex-start',
                        background: decision === "malware" ? 'linear-gradient(135deg, #ff6b35 0%, #f7931e 100%)' : 'transparent'
                      }}
                    >
                      🛡️ Schadsoftware‑Verdacht
                    </Button>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Button 
                      fullWidth
                      variant={decision === "fakeShop" ? "contained" : "outlined"}
                      onClick={() => setDecision("fakeShop")}
                      sx={{ 
                        justifyContent: 'flex-start',
                        background: decision === "fakeShop" ? 'linear-gradient(135deg, #ff6b35 0%, #f7931e 100%)' : 'transparent'
                      }}
                    >
                      🛍️ Fake‑Shop / Paket‑SMS
                    </Button>
                  </Grid>
                </Grid>

                {selectedOutcome && (
                  <Paper sx={{ p: 3, backgroundColor: '#fff3e0', border: '1px solid #ffb74d' }}>
                    <Typography variant="h6" sx={{ color: '#e65100', mb: 2 }}>
                      Empfohlene nächsten Schritte: {selectedOutcome.title}
                    </Typography>
                    <Box component="ul" sx={{ pl: 2 }}>
                      {selectedOutcome.actions.map((action, index) => (
                        <Box component="li" key={index} sx={{ mb: 1 }}>
                          <a 
                            href={action.href} 
                            target="_blank" 
                            rel="noreferrer"
                            style={{ color: '#e65100', textDecoration: 'underline' }}
                          >
                            {action.label}
                          </a>
                        </Box>
                      ))}
                    </Box>
                  </Paper>
                )}

                <Accordion sx={{ mt: 2 }}>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography variant="subtitle1">Wie sichere ich Beweise richtig?</Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Typography variant="body2">
                      Screenshots der Seiten, E‑Mails mitsamt Kopfzeilen, Chatverläufe, Zahlungsbelege, IBAN/Kontonummer, Web‑Adressen. Notiere Datum/Uhrzeit.
                    </Typography>
                  </AccordionDetails>
                </Accordion>

                <Accordion>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography variant="subtitle1">Ich habe meine Daten preisgegeben – was zuerst?</Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Typography variant="body2">
                      Passwörter sofort ändern (E‑Mail, Bank, Shops), 2‑Faktor aktivieren, unübliche Anmeldungen prüfen, ggf. Karten sperren (116 116), Anzeige erstatten.
                    </Typography>
                  </AccordionDetails>
                </Accordion>
              </CardContent>
            </Card>
            */}


            {/* Warning */}
            <Alert severity="warning" sx={{ mt: 2 }}>
              <Typography variant="body2">
                <strong>Hinweis:</strong> Diese Seite stellt keine Rechtsberatung dar. Im Zweifel und bei Gefahr sofort 110 oder 112 anrufen.
              </Typography>
            </Alert>
          </Box>
        </DialogContent>
        
        <DialogActions sx={{ p: 3, gap: 2 }}>
          <Button
            onClick={handleCloseDialog}
            variant="outlined"
            sx={{ 
              borderRadius: '8px',
              px: 3,
              py: 1.5
            }}
          >
            Schließen
          </Button>
        </DialogActions>
      </Dialog>

      {/* Contact List Dialog */}
      <Dialog
        open={showContactDialog}
        onClose={handleCloseContactDialog}
        maxWidth="md"
        fullWidth
        fullScreen={isMobile}
        PaperProps={{
          sx: {
            borderRadius: isMobile ? 0 : '16px',
            background: 'linear-gradient(135deg, #fff8e1 0%, #ffffff 100%)',
            maxHeight: isMobile ? '100vh' : '90vh',
            margin: isMobile ? 0 : '32px'
          }
        }}
      >
        <DialogTitle sx={{ 
          background: 'linear-gradient(135deg, #ff6b35 0%, #f7931e 100%)',
          color: 'white',
          textAlign: 'center',
          py: isMobile ? 2 : 3
        }}>
          <Typography variant={isMobile ? "h5" : "h4"} component="div" sx={{ 
            fontWeight: 'bold', 
            mb: 1,
            fontSize: isMobile ? '1.5rem' : '2.125rem'
          }}>
            📞 Andere wichtige Kontaktdaten
          </Typography>
          <Typography variant="body1" sx={{ 
            opacity: 0.9,
            fontSize: isMobile ? '0.9rem' : '1rem'
          }}>
            Wichtige Telefonnummern für verschiedene Notfälle und Beratungen
          </Typography>
        </DialogTitle>
        
        <DialogContent sx={{ 
          p: isMobile ? 2 : 3, 
          maxHeight: isMobile ? 'calc(100vh - 120px)' : '70vh', 
          overflow: 'auto' 
        }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: isMobile ? 2 : 3 }}>
            {CONTACT_CATEGORIES.map((category, categoryIndex) => (
              <Card key={categoryIndex} sx={{ 
                transition: 'all 0.3s ease',
                '&:hover': { 
                  boxShadow: 4,
                  transform: 'translateY(-2px)'
                }
              }}>
                <CardHeader 
                  title={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {category.icon}
                      <Typography variant="h6" sx={{ 
                        color: '#e65100',
                        fontWeight: 'bold'
                      }}>
                        {category.title}
                      </Typography>
                    </Box>
                  }
                  sx={{ 
                    backgroundColor: '#fff3e0',
                    borderBottom: '1px solid #ffb74d'
                  }}
                />
                <CardContent sx={{ p: isMobile ? 1.5 : 2 }}>
                  <Grid container spacing={isMobile ? 1 : 2}>
                    {category.contacts.map((contact, contactIndex) => (
                      <Grid item xs={12} sm={6} md={4} key={contactIndex}>
                        <Box sx={{
                          p: isMobile ? 1.5 : 2,
                          border: '1px solid #e0e0e0',
                          borderRadius: '8px',
                          backgroundColor: '#fafafa',
                          transition: 'all 0.3s ease',
                          '&:hover': {
                            backgroundColor: '#f0f0f0',
                            borderColor: '#ff6b35'
                          }
                        }}>
                          <Typography variant="subtitle2" sx={{ 
                            fontWeight: 'bold', 
                            mb: 1,
                            fontSize: isMobile ? '0.85rem' : '0.875rem'
                          }}>
                            {contact.name}
                          </Typography>
                          <Typography 
                            variant="h6" 
                            sx={{ 
                              color: '#e65100', 
                              fontWeight: 'bold',
                              mb: 1,
                              cursor: 'pointer',
                              fontSize: isMobile ? '1rem' : '1.25rem',
                              '&:hover': { textDecoration: 'underline' }
                            }}
                            onClick={() => {
                              if (contact.number.startsWith('0800') || contact.number.startsWith('0900') || contact.number === '110' || contact.number === '112' || contact.number === '116 116') {
                                window.location.href = `tel:${contact.number}`;
                              } else {
                                setSnackbar({
                                  open: true,
                                  message: `Rufe ${contact.number} an für: ${contact.description}`,
                                  severity: 'info'
                                });
                              }
                            }}
                          >
                            {contact.number}
                          </Typography>
                          <Typography variant="caption" sx={{ 
                            color: 'text.secondary',
                            fontSize: isMobile ? '0.7rem' : '0.75rem',
                            lineHeight: 1.3
                          }}>
                            {contact.description}
                          </Typography>
                        </Box>
                      </Grid>
                    ))}
                  </Grid>
                </CardContent>
              </Card>
            ))}
            
            <Alert severity="info" sx={{ mt: 2 }}>
              <Typography variant="body2">
                <strong>Hinweis:</strong> Kostenlose Nummern (0800/0900) sind kostenlos. Notrufnummern (110/112) sind immer kostenlos. 
                Andere Nummern können kostenpflichtig sein.
              </Typography>
            </Alert>
          </Box>
        </DialogContent>
        
        <DialogActions sx={{ p: 3, gap: 2 }}>
          <Button
            onClick={handleCloseContactDialog}
            variant="outlined"
            sx={{ 
              borderRadius: '8px',
              px: 3,
              py: 1.5
            }}
          >
            Schließen
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert 
          onClose={handleSnackbarClose} 
          severity={snackbar.severity}
          sx={{ 
            borderRadius: '8px',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)'
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default SOSIconComponent;
